import { VideoHandlerBase, VideoHandlerConfig } from './Abstract';
import { Log } from '../../AuxillaryService/Log';
import { Media, State } from './types';
import { VideoLoadError } from './errors';
import { assert } from '../../Utility/assert';

export type GenericHandlerConfig = VideoHandlerConfig & {
    bsViewModeScaleToFill?: boolean;
};

export type GenericVideo = HTMLVideoElement;

export abstract class GenericVideoHandler extends VideoHandlerBase<GenericVideo> {
    protected abstract _type: string;
    protected _videoUrl?: string;

    /**
     * (BrightSign)
     */
    protected _bsViewModeScaleToFill: boolean;

    constructor(config: GenericHandlerConfig) {
        super(config);
        this._bsViewModeScaleToFill = config.bsViewModeScaleToFill || false;
    }

    protected async _createNewPlayer(): Promise<GenericVideo> {
        const element = this.getElement();

        const player = document.createElement('video');
        element.appendChild(player);

        try {
            await this._loadVideo(player);
        } catch (err) {
            if (err instanceof VideoLoadError) {
                Log.Debug(
                    `[${this.id}] Remove player element after it failed to load. ${err.message}`,
                    null,
                    __fileline,
                    { error: err }
                );
                await this._unloadVideo(player);
                player.parentNode?.removeChild(player);
            }
            throw err;
        }

        player.addEventListener('stalled', (event) => {
            assert(event.target instanceof HTMLVideoElement, 'Target is not a Video element');

            const isActive = this._state === State.PLAYING;
            const source = event.target.src;
            const currentTime = event.target.currentTime;
            const elementId = event.target.id;

            if (isActive) {
                Log.Warning("Attempting to fix 'stalled' video", null, __fileline, {
                    source: source,
                    currentTime: currentTime,
                    id: elementId,
                });

                event.target.load(); // reload video
                event.target.currentTime = currentTime; // this code might not do anything in this context
                event.target.play();
            }
        });

        player.addEventListener('error', (event) => {
            assert(event.target instanceof HTMLVideoElement, 'Target is not a Video element');
            Log.Warning(
                `[${this.id}] An error occurred: ${parseMediaError(event.target.error)}`,
                null,
                __fileline,
                event.target.error
            );
        });

        player.addEventListener('ended', () => {
            this.eventEmitter.emitEvent(Media.Ended);
        });

        // TODO: remove since 'scale-to-fill' is now the default in FW 7.0+
        if (this._bsViewModeScaleToFill) {
            player.setAttribute('viewmode', 'scale-to-fill');
        }

        if (this._muted) {
            player.muted = true;
        }

        if (this._showControls) {
            player.controls = true;
        }

        return player;
    }

    protected async _setCurrentTime(seconds: number): Promise<void> {
        const player = this._getAttachedPlayer();
        player.currentTime = seconds;
    }

    protected async _doPlay(): Promise<void> {
        const player = this._getAttachedPlayer();
        await player.play();
    }

    protected async _doPause(): Promise<void> {
        const player = this._getAttachedPlayer();
        await player.pause();
    }

    protected async _doDestroyPlayer(): Promise<void> {
        const player = this._getPossiblyDetachedPlayer();
        await this._unloadVideo(player);
    }

    protected _loadVideo(player: HTMLVideoElement): Promise<void> {
        assert(this._videoUrl !== undefined, 'Missing Video URL.');
        return loadVideo(this._videoUrl, player, this.id);
    }

    protected _unloadVideo(player: HTMLVideoElement): Promise<void> {
        return unloadVideo(player, this.id);
    }
}

/**
 *
 * @throws {@link VideoLoadError} when there is an error loading the video
 */
async function loadVideo(
    videoPath: string,
    player: HTMLVideoElement,
    identity: string
): Promise<void> {
    await new Promise<void>((resolve, reject) => {
        player.addEventListener(
            'loadedmetadata',
            (event) => {
                assert(event.target instanceof HTMLVideoElement, 'Target is not a Video element');
                Log.Debug(`[${identity}] Player Ready.`, null, __fileline, event.target.currentSrc);
                resolve();
            },
            { once: true }
        );

        player.addEventListener(
            'error',
            (event) => {
                assert(event.target instanceof HTMLVideoElement, 'Target is not a Video element');
                const message = event.target.error?.message || 'Unknown Error.';
                reject(new VideoLoadError(message));
            },
            { once: true }
        );

        player.src = videoPath;
        player.load();
    });
}

async function unloadVideo(player: HTMLVideoElement, identity: string): Promise<void> {
    // Make sure the browser unloads the video (http://stackoverflow.com/a/28112583)
    // Step 1) Pause the video
    // Step 2) Clear the video source URL
    // Step 3) Tell the video to start loading "nothing"
    player.pause(); // Step 1
    try {
        await loadVideo('', player, identity); // Steps 2 & 3
    } catch (err) {
        if (err instanceof VideoLoadError) {
            // This is what we are expecting. We are setting the `src` to
            // empty, so we should get an error complaining about that.
            Log.Debug(`[${identity}] Player Unloaded with error. ${String(err)}`, null, __fileline);
        } else {
            throw err;
        }
    }
}

function parseMediaError(error: MediaError | null) {
    const code = error?.code ?? -1;
    const message = error?.message ?? 'Unknown Error.';

    let codeStr = 'UNKNOWN';
    switch (code) {
        case MediaError.MEDIA_ERR_ABORTED:
            codeStr = 'MEDIA_ERR_ABORTED';
            break;
        case MediaError.MEDIA_ERR_NETWORK:
            codeStr = 'MEDIA_ERR_NETWORK';
            break;
        case MediaError.MEDIA_ERR_DECODE:
            codeStr = 'MEDIA_ERR_DECODE';
            break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            codeStr = 'MEDIA_ERR_SRC_NOT_SUPPORTED';
            break;
    }

    return `${message} (${codeStr}<${code}>)`;
}
